import { SecureStorage } from "@aparajita/capacitor-secure-storage";
import type { PersistedEnvelope } from "../types";
import type { StorageBackend } from "./StorageBackend";

/** Prefix for secure-storage keys (mirrors Preferences backend naming). */
const KEY_PREFIX = "flowride.persist.";

/**
 * Returns whether a secure-storage key belongs to this persistence module.
 *
 * @param key Full key from `SecureStorage.keys()`.
 */
const isFlowridePersistKey = ( key: string ): boolean => key.startsWith( KEY_PREFIX );

/**
 * Strips {@link KEY_PREFIX} to return a slice registry key.
 *
 * @param key Full secure-storage key.
 */
const toSliceKeyFromSecureKey = ( key: string ): string => key.slice( KEY_PREFIX.length );

/**
 * Secure storage backend for sensitive slices (e.g. auth token).
 *
 * @remarks Native: Keychain / Keystore. Web dev: plugin localStorage fallback (not encrypted).
 */
export const secureStorageBackend: StorageBackend = {
    "kind": "secure",

    /**
     * Reads and JSON-parses an envelope from secure storage.
     *
     * @typeParam T Expected `data` shape.
     * @param key Slice key.
     */
    async get<T>( key: string ): Promise<PersistedEnvelope<T> | null> {
        const raw = await SecureStorage.getItem( `${ KEY_PREFIX }${ key }` );
        if ( raw === null ) {
            return null;
        }

        try {
            return JSON.parse( raw ) as PersistedEnvelope<T>;
        } catch( error: unknown ) {
            console.error( `[SecureStorageBackend] Failed to parse envelope for ${ key }`, error );

            return null;
        }
    },

    /**
     * Stores a JSON-serialized envelope in secure storage.
     *
     * @typeParam T Envelope `data` type.
     * @param key Slice key.
     * @param envelope Versioned blob.
     */
    async set<T>( key: string, envelope: PersistedEnvelope<T> ): Promise<void> {
        await SecureStorage.setItem( `${ KEY_PREFIX }${ key }`, JSON.stringify( envelope ) );
    },

    /**
     * Removes the secure-storage entry for a slice.
     *
     * @param key Slice key.
     */
    async del( key: string ): Promise<void> {
        await SecureStorage.remove( `${ KEY_PREFIX }${ key }` );
    },

    /**
     * Lists slice keys stored under {@link KEY_PREFIX}.
     */
    async keys(): Promise<string[]> {
        const allKeys = await SecureStorage.keys();

        return allKeys.filter( isFlowridePersistKey ).map( toSliceKeyFromSecureKey );
    },
};
